import {
  accountTypes,
  getCorrectedMailingAddress,
  getCorrectedResidentialAddress,
  getOriginalMailingAddress,
  getOriginalResidentialAddress,
  parsePhone,
  PhoneNumberType,
  getAppSourceType,
  parseCustomerInformation,
  secondaryCommitMessage,
  applicationStatusTypes,
  fundingTypes
} from '../../shared/utils';
import {
  pollForSubmissionResponse,
  appendMailingAddressIfDifferent,
  commitConsent,
  getEnablementStatus,
  loadUser,
  populateResidentialAddress,
  getAdditionalCustomersExistingAgreements
} from '../../shared/loaders';
import { merge, mergeTyped, withDefault } from '../../../core/util';

import { commitNewInvestmentsBYOP } from '../shared/loaders';
import { DataLoader } from '../../../core/types/data-loader';
import {
  StateStoreOrVoid,
  ApplicationState,
  PartialStateStore,
  StateStore
} from '../../../core/types/application-state';
import { postAddressValidation } from '../../shared/services/address-validation-service';
import { JointApplicationService } from '../../shared/services/mutualfund-taxable-joint';
import {
  analyticsDataLayer,
  dispatchPageLoad
} from '../../../analytics/analytics-helper';
import { productTypesAnalytics, TODOrDefault } from '../../../analytics/util';
import {
  parseAdditionalCustomers,
  parseJointOwnerInformation,
  parseJointOwnerTaxIDs
} from '../shared/util';
import { encode_deviceprint as encodeDeviceprint } from '../../../rsa-fingerprint';
import { retrieveFilterAndAssignAppID } from '../../shared/services/profile-service';

export const makeLoadProfileData = (): DataLoader =>
  async function loadProfileData(
    a: ApplicationState
  ): Promise<StateStoreOrVoid> {
    let existingProfileAppID = '';
    const userResults = await loadUser(a);

    if (
      userResults?.app?.customer?.primaryEmailAddress &&
      userResults?.authenticated
    ) {
      existingProfileAppID = await retrieveFilterAndAssignAppID(
        userResults?.app?.customer?.primaryEmailAddress,
        accountTypes.JOINT
      );
    }

    return {
      ...mergeTyped<StateStoreOrVoid>(userResults, {
        accountType: accountTypes.JOINT,
        app: {
          profileApplicationID: existingProfileAppID
        }
      })
    };
  };

/* eslint-disable */
export const commitJointOwnerPersonalInformation = async (
  appState
): Promise<PartialStateStore> => {
  const {
    inputs,
    store: {
      enabled,
      amount,
      app: { customer, jointOwners },
      authenticated
    },
    chosen
  } = appState;

  const { dob, people } = inputs;

  let res;

  if (Array.isArray(chosen)) {
    res = commitNewInvestmentsBYOP(appState);
  } else {
    const parsedJointOwnerTaxIds = parseJointOwnerTaxIDs(people, jointOwners);

    const enablementStatus = await getEnablementStatus(appState);

    const existingAccountAgreementsResults: any[] = await getAdditionalCustomersExistingAgreements(
      {
        authenticated,
        taxID: parsedJointOwnerTaxIds,
        accountType: accountTypes.JOINT
      }
    );

    res = merge(
      {
        amount,
        ...(existingAccountAgreementsResults && {
          existingAgreements: existingAccountAgreementsResults
        }),
        app: {
          customer: parseCustomerInformation(inputs, customer, enabled),
          jointOwners: parseJointOwnerInformation(people, jointOwners)
        },
        // since a joint owner is required we should always update the additionalCustomers object
        // when user navigates forward from PI Screen
        additionalCustomers: parseAdditionalCustomers(people, jointOwners)
      },
      enablementStatus
    );
  }

  return res;
};

export const commitAddressValidation = async ({
  inputs,
  store
}): Promise<StateStore> => {
  const { app, additionalCustomers } = store;
  const { customer, jointOwners = [] } = app;
  const residentialStreet1 = withDefault(inputs.residentialStreet1, '');
  const residentialStreet2 = withDefault(inputs.residentialStreet2, '');
  const residentialZip = withDefault(inputs.residentialZip, '');
  const residentialCity = withDefault(inputs.residentialCity, '');
  const residentialState = withDefault(inputs.residentialState, '');
  const {
    mailingAddressDifferent,
    people,
    phone1,
    phone2,
    phone3,
    primaryEmailAddress
  } = inputs;

  const residentialAddress = {
    city: residentialCity.trim(),
    country: 'United States',
    state: residentialState.trim(),
    street1: residentialStreet1.trim(),
    street2: residentialStreet2?.trim(),
    street3: '',
    usaddress: true,
    zip: residentialZip.trim()
  };

  let mailingAddress = {};
  if (mailingAddressDifferent) {
    const mailingStreet1 = withDefault(inputs.mailingStreet1, '');
    const mailingStreet2 = withDefault(inputs.mailingStreet2, '');
    const mailingZip = withDefault(inputs.mailingZip, '');
    const mailingCity = withDefault(inputs.mailingCity, '');
    const mailingState = withDefault(inputs.mailingState, '');

    mailingAddress = {
      city: mailingCity.trim(),
      country: 'United States',
      state: mailingState.trim(),
      street1: mailingStreet1.trim(),
      street2: mailingStreet2?.trim(),
      street3: '',
      usaddress: true,
      zip: mailingZip.trim()
    };
  }

  let addresses = [residentialAddress];
  addresses = appendMailingAddressIfDifferent(
    addresses,
    mailingAddressDifferent,
    mailingAddress
  );

  const phones = [
    { phone: phone1, type: PhoneNumberType.CELLULAR },
    {
      phone: phone2,
      type: PhoneNumberType.DAYTIME
    },
    { phone: phone3, type: PhoneNumberType.EVENING }
  ]
    .filter(({ phone }) => phone)
    .map(({ phone, type }) => parsePhone(phone, type));

  const [
    residentialAddressValid,
    mailingAddressValid
  ] = await postAddressValidation(addresses);

  const residentialAddressValidation =
    residentialAddressValid || residentialAddress;
  const mailingAddressValidation = mailingAddressValid || mailingAddress;

  // need to commit email address in the case user has updated it since basic pi
  // otherwise modal action will wipe any user updates to email address
  const res = {
    residentialAddressValidation,
    ...(mailingAddressDifferent && { mailingAddressValidation }),
    mailingAddressDifferent,
    app: {
      customer: {
        ...customer,
        phones,
        primaryEmailAddress
      },
      jointOwners: people?.map((person, idx) => ({
        ...jointOwners[idx],
        residentialAddress: person?.jointOwnerSameAddress
          ? residentialAddressValidation
          : {
              street1: person?.jointOwnerResidentialStreet1.trim(),
              street2: person?.jointOwnerResidentialStreet2?.trim(),
              state: person?.jointOwnerResidentialState.trim(),
              zip: person?.jointOwnerResidentialZip.trim(),
              city: person?.jointOwnerResidentialCity.trim()
            }
      }))
    },
    additionalCustomers: people?.map((person, idx) => ({
      ...additionalCustomers[idx],
      residentialAddress: person?.jointOwnerSameAddress
        ? residentialAddressValidation
        : {
            street1: person?.jointOwnerResidentialStreet1.trim(),
            street2: person?.jointOwnerResidentialStreet2?.trim(),
            state: person?.jointOwnerResidentialState.trim(),
            zip: person?.jointOwnerResidentialZip.trim(),
            city: person?.jointOwnerResidentialCity.trim()
          },
      sameAddress: withDefault(person?.jointOwnerSameAddress, false)
    }))
  };
  return res;
};

export const commitJointOwnerContactInformation: DataLoader = async ({
  inputs,
  chosen,
  store
}): Promise<StateStore> => {
  if (chosen?.section === 'addressValidation') {
    return commitAddressValidation({ inputs, store });
  }

  const { useResidentialAddress, useMailingAddress } = inputs;
  const {
    residentialAddressValidation,
    mailingAddressValidation = {},
    mailingAddressDifferent
  } = store;

  const originalResidentialAddress = getOriginalResidentialAddress(
    residentialAddressValidation
  );
  const correctedResidentialAddress = getCorrectedResidentialAddress(
    residentialAddressValidation
  );
  const residentialAddress = populateResidentialAddress(
    useResidentialAddress,
    originalResidentialAddress,
    correctedResidentialAddress
  );
  const originalMailingAddress = getOriginalMailingAddress(
    mailingAddressValidation
  );
  const correctedMailingAddress = getCorrectedMailingAddress(
    mailingAddressValidation
  );

  let mailingAddress = residentialAddress;
  if (mailingAddressDifferent) {
    if (useMailingAddress === 'useOriginalMailingAddress') {
      const { street1, street2, city, state, zip } = originalMailingAddress;
      mailingAddress = {
        street1,
        street2,
        city,
        state,
        zip,
        corrected: false
      };
    } else {
      const {
        street1,
        street2,
        city,
        state,
        zip,
        zip4
      } = correctedMailingAddress;
      mailingAddress = {
        street1,
        street2,
        city,
        state,
        zip,
        zip4,
        corrected: true
      };
    }
  }

  const {
    app: { jointOwners = [] },
    additionalCustomers = []
  } = store;

  const getSameAddress = index => additionalCustomers[index]?.sameAddress;

  const res = {
    app: {
      customer: {
        residentialAddress,
        mailingAddress
      },
      jointOwners: jointOwners.map((jo, idx) => ({
        ...jointOwners[idx],
        residentialAddress: getSameAddress(idx)
          ? residentialAddress
          : jo.residentialAddress
      }))
    },
    additionalCustomers: additionalCustomers.map((customer, idx) => ({
      ...additionalCustomers[idx],
      residentialAddress: customer.sameAddress
        ? residentialAddress
        : customer?.residentialAddress,
      sameAddress: customer?.sameAddress
    }))
  };
  return res;
};
/* eslint-enable */

export const createJointApplication: DataLoader = async ({
  inputs,
  store,
  chosen
}): Promise<StateStoreOrVoid> => {
  if (
    ['byop', 'funding', 'info', 'beneficiaries', 'joint-owners'].includes(
      chosen
    )
  ) {
    return { inReviewMode: true };
  }

  const { app, consumerName } = store;
  const { customer, termsIDs } = app;
  const { eligibleForTaxWithholding = false } = inputs;
  // use terms ids to create consent
  await commitConsent(termsIDs, customer);
  // terms ids should be an array of strings for service
  const stringTermsIDs = termsIDs?.map(id => id?.toString());
  // add term IDs to account creation service payload
  const updatedAppState = {
    ...app,
    fundingMethodType: fundingTypes.FUND_LATER,
    eligibleForTaxWithholding,
    termsIDs: stringTermsIDs,
    deviceToken: encodeDeviceprint()
  };

  // get the service error code and tracking id on the initial submission request
  const {
    serviceErrorCode,
    trackingIdentifier
  } = await JointApplicationService(updatedAppState, consumerName);

  const isSubmissionTimedOut = serviceErrorCode === 504;

  if (isSubmissionTimedOut) {
    return {
      loadedData: {
        is504Timeout: true
      },
      app: {
        applicationStatus: applicationStatusTypes.COMPLETED
      }
    };
  }

  const submissionResult = await pollForSubmissionResponse(trackingIdentifier);
  return submissionResult;
};
createJointApplication.message = 'Creating your new account';
createJointApplication.secondaryMessage = secondaryCommitMessage;

export const updateAnalytics: DataLoader = async (
  { store },
  flowProps
): Promise<StateStoreOrVoid> => {
  analyticsDataLayer({
    accountType: TODOrDefault(store?.app?.beneficiaries, 'JOINT'),
    productType: productTypesAnalytics.MUTUAL_FUND,
    sourceType: getAppSourceType(store?.consumerName),
    userType:
      store?.authenticated && !store?.loadedData?.newUserCreated
        ? 'Existing'
        : 'New',
    amount: store?.amount,
    isComplete: Boolean(flowProps?.analytics?.isComplete),
    clientTier: store?.summitData?.assetTierID?.toString()
  });
  dispatchPageLoad(flowProps?.analytics.pageName);
};
